import { ApolloError } from "@apollo/client";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import { HexMenuItem, Txt } from "../../../hex-components";
import {
  HexCollapse,
  ToggleHeader,
} from "../../../hex-components/HexCollapse.js";
import {
  LocalStorageKeys,
  useLocalStorage,
} from "../../../hooks/useLocalStorage.js";
import { useToggleState } from "../../../hooks/useToggleState";
import { SIDEBAR_PADDING } from "../../sidebar/sidebarSharedStyles.js";
import { MoreMenuIcon } from "../../icons/CustomIcons";

const StyledCollapse = styled(HexCollapse)`
  width: 100%;

  li {
    list-style-type: none;
  }

  & ${ToggleHeader} {
    justify-content: flex-start;
    gap: 4px;
  }

  && {
    margin: 0;
  }

  > :first-child {
    padding-top: ${SIDEBAR_PADDING / 2}px;
  }
`;

const Header = styled.div`
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSize.HEADING_FIVE};
  color: ${({ theme }) => theme.fontColor.MUTED};
  padding-left: 4px;
`;

const EmptyFavorite = styled.div`
  color: ${({ theme }) => theme.fontColor.PLACEHOLDER};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  padding: 7px 4px;
  padding-left: 12px;
  min-height: 30px;
`;

type SidebarCollapsedStateFromStorage = Pick<
  typeof LocalStorageKeys,
  | "SIDEBAR_FAVORITE_COLLECTIONS_OPENED"
  | "SIDEBAR_FAVORITE_PROJECTS_OPENED"
  | "SIDEBAR_FAVORITE_DATA_OPENED"
  | "SIDEBAR_RECENT_ITEMS_OPENED"
>;
const SHOW_MORE_WHEN_MORE_THAN = 8;
const SHOW_MORE_NUM_ITEMS_COLLAPSED = 5;

interface SidebarSectionProps {
  items: React.ReactNode[];
  loading: boolean;
  error: ApolloError | undefined;
  text: string;
  /**
   * Boolean LocalStorage key used to indicate whether or not the sidebar section
   * should be opened or closed.
   */
  localStorageKey: keyof SidebarCollapsedStateFromStorage;
  hideIfEmpty?: boolean;
}
export const SidebarSection: React.FunctionComponent<SidebarSectionProps> = ({
  error,
  hideIfEmpty = false,
  items,
  loading,
  localStorageKey,
  text,
}) => {
  const [isOpened, setIsOpened] = useLocalStorage(
    LocalStorageKeys[localStorageKey],
  );
  const [showMore, _, { toggle: toggleShowMore }] = useToggleState(false);

  const toggleCallback = useCallback(() => {
    setIsOpened(!isOpened);
  }, [setIsOpened, isOpened]);

  const shouldShowMore = items.length > SHOW_MORE_WHEN_MORE_THAN;

  const displayedItems = useMemo(() => {
    if (showMore) {
      return items;
    }
    return items.slice(0, SHOW_MORE_NUM_ITEMS_COLLAPSED);
  }, [items, showMore]);

  if (loading || error) {
    return null;
  }

  if (hideIfEmpty && items.length === 0) {
    return null;
  }

  return (
    <StyledCollapse
      isOpen={isOpened}
      small={true}
      title={<Header>{text}</Header>}
      toggleIsOpen={toggleCallback}
      togglePosition="right"
    >
      {items.length === 0 ? (
        <EmptyFavorite>{text} will appear here.</EmptyFavorite>
      ) : (
        <>
          {displayedItems}
          {shouldShowMore && (
            <HexMenuItem
              icon={<MoreMenuIcon />}
              text={<Txt fontColor="muted">{showMore ? "Less" : "More"}</Txt>}
              onClick={toggleShowMore}
            />
          )}
        </>
      )}
    </StyledCollapse>
  );
};
